import React, { FC, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse } from '@mui/material';
import { BasicButton } from '../../commons/buttons/BasicButton/BasicButton';

import { CustomNumberFormat } from '../../commons/CustomNumberFormat';
import { ExAmoView, PreparedCalculateData } from '../../../models/Calculate';
import { ReactComponent as ArrowOpen } from '../../../assets/images/arrows/ArrowClosed.svg';

interface BillContentProps {
  calculation: PreparedCalculateData | null;
  userBalance: number;
  userBonuses: number;
  billDiscountPercent: number;
  serviceName: string;
  isShowEarnBonuses: boolean;
}

const BillContent: FC<BillContentProps> = ({
  calculation,
  userBalance,
  userBonuses,
  billDiscountPercent,
  serviceName,
  isShowEarnBonuses,
}) => {
  const { t } = useTranslation();
  const {
    view,
    discount_amount: discountAmount,
    earn_bonus_amount: earnBonusAmount,
    back_bonus_amount: backBonusAmount,
    back_amount: backAmount,
    can_balance_amount: canBalanceAmount,
    bonus_amount: bonusAmount,
    pay_amount: payAmount,
  } = calculation ?? {};

  // const exServices = useExServices(view || defaultAccountView, monthCount || 1);
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="collapse-btn">
        <BasicButton handleClick={() => setOpen((prev) => !prev)} className={`btn-props ${open ? '' : 'closed'}`}>
          <ArrowOpen className={`${open ? 'open' : ''}`} />
        </BasicButton>
      </div>
      <Collapse in={open} timeout={500}>
        <div className="card">
          <hr />
          <div className="info-row">
            <article className="payment__content__label">{serviceName}</article>
            <div className="payment__content__value">
              <CustomNumberFormat value={view?.service_amo || 0} suffix={t('common.uah')} />
            </div>
          </div>
          {calculation?.view.ex_amo &&
            [...calculation.view.ex_amo]
              .sort((a, b) => b.service_amo - a.service_amo)
              .map((srv: ExAmoView) => {
                return (
                  <div key={srv.id} className="info-row">
                    <article className="payment__content__label">{srv.name}</article>
                    <div className="payment__content__value">
                      <CustomNumberFormat value={srv.service_amo} suffix={t('common.uah')} />
                    </div>
                  </div>
                );
              })}
          {discountAmount && discountAmount > 0 ? (
            <div className="info-row">
              <article className="payment__content__label">
                {t('common.discount')} {billDiscountPercent ? `${billDiscountPercent}%` : null}
              </article>
              <div className="payment__content__value">
                <CustomNumberFormat value={-1 * discountAmount} suffix={t('common.uah')} />
              </div>
            </div>
          ) : null}
          {isShowEarnBonuses ? (
            <div className="info-row">
              <article className="payment__content__label">{t('common.bonusesAccrued')}</article>
              <div className="payment__content__value">
                <CustomNumberFormat value={earnBonusAmount || 0} />
              </div>
            </div>
          ) : null}
          {(backAmount && backAmount > 0) || (backBonusAmount && backBonusAmount > 0) ? (
            <>
              <hr />
              {backAmount && backAmount > 0 ? (
                <>
                  <div className="info-row">
                    <div className="info-row__refund">
                      <article className="payment__content__label">{t('servicePayment.backAmount')}</article>
                    </div>
                    <div className="payment__content__value">
                      <CustomNumberFormat value={backAmount} suffix={t('common.uah')} />
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-row__refund">
                      <article className="payment__content__label">{t('servicePayment.totalBalance')}</article>
                    </div>
                    <div className="payment__content__value">
                      <CustomNumberFormat value={backAmount + userBalance} suffix={t('common.uah')} />
                    </div>
                  </div>
                </>
              ) : null}
              {backBonusAmount && backBonusAmount > 0 ? (
                <>
                  <div className="info-row">
                    <div className="info-row__refund">
                      <article className="payment__content__label">{t('servicePayment.backBonusAmount')}</article>
                    </div>
                    <div className="payment__content__value">
                      <CustomNumberFormat value={backBonusAmount} />
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-row__refund">
                      <article className="payment__content__label">{t('servicePayment.totalBonuses')}</article>
                    </div>
                    <div className="payment__content__value">
                      <CustomNumberFormat value={backBonusAmount + userBonuses} />
                    </div>
                  </div>
                </>
              ) : null}
            </>
          ) : null}
          <hr />
          {canBalanceAmount && canBalanceAmount > 0 ? (
            <div className="info-row">
              <div className="info-row__refund">
                <article className="payment__content__label">{t('servicePayment.cashWithdrawal')}</article>
              </div>
              <div className="payment__content__value">
                <CustomNumberFormat value={-1 * canBalanceAmount} suffix={t('common.uah')} />
              </div>
            </div>
          ) : null}
          {bonusAmount && bonusAmount > 0 ? (
            <div className="info-row">
              <article className="payment__content__label">{t('servicePayment.bonusesWithdrawal')}</article>
              <div className="payment__content__value">
                <CustomNumberFormat value={-1 * bonusAmount} />
              </div>
            </div>
          ) : null}
          <div className="info-row">
            <article className="payment__content__label">{t('servicePayment.sum')}</article>
            <div className="payment__content__value payment__content__value__strong">
              <CustomNumberFormat value={payAmount || 0} suffix={t('common.uah')} />
            </div>
          </div>
        </div>
      </Collapse>
    </>
  );
};

export default memo(BillContent);
